import { useState, useCallback, useRef, useEffect } from "react";
import { useMutation } from "@apollo/client";
import {
  GET_PERMIT_DOCUMENT_UPLOAD_URL,
  GetPermitDocumentUploadURLData,
  GetPermitDocumentUploadURLVars,
} from "common/graphql/mutations";
import { UploadStatus, UploadedFile } from "./types";

const ALLOWED_FILE_TYPES = ["application/pdf", "image/jpeg", "image/png"];
const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png"];
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

interface UseDocumentUploadOptions {
  payeeAccountId: string;
  documentTypeId: string;
  onUploadComplete: (documentId: string) => void;
  onClear: () => void;
}

interface UseDocumentUploadResult {
  status: UploadStatus;
  progress: number;
  uploadedFile: UploadedFile | null;
  errorMessage: string | null;
  handleFileSelect: (file: File) => void;
  handleRemove: () => void;
  handleRetry: () => void;
}

function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf(".");
  return lastDot >= 0 ? filename.slice(lastDot).toLowerCase() : "";
}

function validateFile(file: File): string | null {
  const extension = getFileExtension(file.name);

  if (!ALLOWED_FILE_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(extension)) {
    return `Invalid file type. Accepted: ${ALLOWED_EXTENSIONS.join(", ").toUpperCase()}`;
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `File too large. Maximum size is ${MAX_FILE_SIZE_MB}MB`;
  }

  return null;
}

export function useDocumentUpload({
  payeeAccountId,
  documentTypeId,
  onUploadComplete,
  onClear,
}: UseDocumentUploadOptions): UseDocumentUploadResult {
  const [status, setStatus] = useState<UploadStatus>("idle");
  const [progress, setProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const pendingFileRef = useRef<File | null>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);

  const [getUploadUrl] = useMutation<GetPermitDocumentUploadURLData, GetPermitDocumentUploadURLVars>(
    GET_PERMIT_DOCUMENT_UPLOAD_URL,
  );

  const uploadToS3 = useCallback(
    async (presignedUrl: string, file: File, documentId: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;

        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            setProgress(percentComplete);
          }
        });

        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            setUploadedFile({
              name: file.name,
              size: file.size,
              type: file.type,
            });
            setStatus("success");
            onUploadComplete(documentId);
            resolve();
          } else if (xhr.status === 403) {
            reject(new Error("Upload URL expired. Please try again."));
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener("error", () => {
          reject(new Error("Network error during upload. Please check your connection."));
        });

        xhr.addEventListener("abort", () => {
          reject(new Error("Upload cancelled"));
        });

        xhr.open("PUT", presignedUrl, true);
        xhr.setRequestHeader("Content-Type", file.type);
        xhr.send(file);
      });
    },
    [onUploadComplete],
  );

  const performUpload = useCallback(
    async (file: File) => {
      setStatus("uploading");
      setProgress(0);
      setErrorMessage(null);
      pendingFileRef.current = file;

      try {
        const result = await getUploadUrl({
          variables: {
            payeeAccountId,
            documentTypeId,
            filename: file.name,
            contentType: file.type,
            fileSize: file.size,
          },
        });

        if (!result.data?.permitDocumentUploadURL) {
          throw new Error("Failed to get upload URL");
        }

        const { presignedURL: presigned_url, id } = result.data.permitDocumentUploadURL;
        await uploadToS3(presigned_url, file, id);
      } catch (error) {
        setStatus("error");
        setErrorMessage(error instanceof Error ? error.message : "Upload failed. Please try again.");
      }
    },
    [getUploadUrl, payeeAccountId, documentTypeId, uploadToS3],
  );

  const handleFileSelect = useCallback(
    (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setStatus("error");
        setErrorMessage(validationError);
        pendingFileRef.current = null;
        return;
      }

      performUpload(file);
    },
    [performUpload],
  );

  const handleRemove = useCallback(() => {
    if (xhrRef.current) {
      xhrRef.current.abort();
      xhrRef.current = null;
    }

    setStatus("idle");
    setProgress(0);
    setUploadedFile(null);
    setErrorMessage(null);
    pendingFileRef.current = null;
    onClear();
  }, [onClear]);

  const handleRetry = useCallback(() => {
    const pendingFile = pendingFileRef.current;
    if (pendingFile) {
      performUpload(pendingFile);
    }
  }, [performUpload]);

  // Cleanup: abort ongoing upload if component unmounts
  useEffect(() => {
    return () => {
      if (xhrRef.current) {
        xhrRef.current.abort();
        xhrRef.current = null;
      }
    };
  }, []);

  return {
    status,
    progress,
    uploadedFile,
    errorMessage,
    handleFileSelect,
    handleRemove,
    handleRetry,
  };
}

export default useDocumentUpload;
